/**
 * EmailTagInput
 * -------------
 * Feb 2026 — lightweight add-by-tag email input. Used on the
 * Division settings page (`AdminCategories.jsx`) to collect the
 * per-division order-notification recipient list.
 *
 * Interaction:
 *   • Type an email and press Enter, comma or Tab to add it as a chip.
 *   • Paste a chunk of emails (comma/newline/semicolon separated) —
 *     each valid one becomes a chip; invalids surface a toast.
 *   • Click the × on any chip to remove it. Backspace on an empty
 *     text field removes the last chip.
 *
 * Value is passed in via `value` (array of strings) and mutations flow
 * back through `onChange(nextArray)`. Fully controlled.
 */
import React, { useState } from 'react';
import { X, Mail } from 'lucide-react';

// Same regex used server-side in `_DIVISION_EMAIL_RE`.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailTagInput = ({
  value = [],
  onChange,
  placeholder = 'Type an email and press Enter…',
  testId = 'email-tag-input',
  disabled = false,
}) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const addEmails = (chunk) => {
    const parts = String(chunk || '')
      .split(/[,;\n\r\t]+/)
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);
    if (!parts.length) return;
    const invalid = [];
    const next = [...value];
    for (const p of parts) {
      if (!EMAIL_RE.test(p)) { invalid.push(p); continue; }
      if (!next.includes(p)) next.push(p);
    }
    if (invalid.length) {
      setError(`Not a valid email: ${invalid.join(', ')}`);
    } else {
      setError('');
    }
    if (next.length !== value.length) onChange(next);
  };

  const removeAt = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      if (draft.trim()) {
        e.preventDefault();
        addEmails(draft);
        setDraft('');
      }
    } else if (e.key === 'Backspace' && !draft && value.length) {
      removeAt(value.length - 1);
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (text && /[,;\n\r\t]/.test(text)) {
      e.preventDefault();
      addEmails(text);
      setDraft('');
    }
  };

  const handleBlur = () => {
    if (draft.trim()) {
      addEmails(draft);
      setDraft('');
    }
  };

  return (
    <div className="w-full" data-testid={testId}>
      <div
        className={`flex flex-wrap items-center gap-1.5 min-h-[42px] px-2 py-1.5 rounded-lg border ${
          error ? 'border-red-400 bg-red-50/40' : 'border-neutral-300 bg-white'
        } focus-within:border-[#0078D4] focus-within:ring-2 focus-within:ring-[#0078D4]/30`}
      >
        {value.map((e, i) => (
          <span
            key={`${e}-${i}`}
            className="inline-flex items-center gap-1 rounded-full bg-[#0078D4]/10 text-[#0078D4] px-2 py-0.5 text-xs font-semibold"
            data-testid={`${testId}-chip-${i}`}
          >
            <Mail className="w-3 h-3" />
            {e}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="hover:bg-[#0078D4]/20 rounded-full p-0.5"
                aria-label={`Remove ${e}`}
                data-testid={`${testId}-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (error) setError(''); }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={value.length ? '' : placeholder}
          className="flex-1 min-w-[160px] px-1 py-1 text-sm bg-transparent focus:outline-none"
          data-testid={`${testId}-input`}
        />
      </div>
      {error ? (
        <div className="mt-1 text-[11px] text-red-600" data-testid={`${testId}-error`}>{error}</div>
      ) : (
        <div className="mt-1 text-[11px] text-neutral-500">
          Press Enter, comma or Tab to add. Paste multiple emails separated by commas or newlines.
        </div>
      )}
    </div>
  );
};

export default EmailTagInput;
